import { useMemo } from 'react';
import { User, SlidersHorizontal } from 'lucide-react';
import { usePlugins } from '@/hooks/config/usePlugins';
import { ROUTES, withOrgSlug } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import type { NavItem } from './nav-types';

/**
 * The plugin-contributed account (user-menu) items, resolved the way the
 * account surfaces render them: org-slug scoped (unless the item is a hash
 * command or declared `rootLevel`), badge lifted into the `trailing` slot, and
 * ordered by the plugin-provided `order` weight. Shared by the account
 * dropdown (`UserSection`) and the Account takeover shell
 * (`AccountShell`) so both surfaces stay in lockstep — one source of truth
 * for the account option set, its routes, and its ordering.
 */
export function useUserMenuItems(): NavItem[] {
  const { navItems: pluginNavItems } = usePlugins();
  const slug = useActiveOrgSlug();
  return useMemo<NavItem[]>(
    () =>
      pluginNavItems
        .filter((i) => i.group === 'user-menu')
        .map((i) => ({
          ...i,
          to: i.to.startsWith('#') || i.rootLevel ? i.to : withOrgSlug(slug, i.to),
          // Plugin-contributed badge lands in the host's `trailing` slot so
          // the menu reuses the same React-render path for built-in and
          // plugin-contributed trailing components.
          trailing: i.badge,
        }))
        // Order by the plugin-provided `order` weight (lower first) so menu
        // placement is independent of plugin registration order. Items with
        // the same weight keep their relative (registration) order via the
        // stable sort.
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [pluginNavItems, slug],
  );
}

/**
 * The subset of account items the dropdown keeps: things the Account takeover
 * cannot host, or cannot host well.
 *
 * - Hash commands (`#send-feedback`) are actions, not pages, and
 *   {@link useAccountNavItems} drops them from the takeover nav — the
 *   dropdown is their only menu home.
 * - Badged items (Notifications) carry live state, which is the reason people
 *   open this menu without a destination in mind.
 *
 * Everything else is a page. Pages are one click away in the takeover's nav
 * (reached via the Account row) and in the command palette, so listing them
 * here as well only makes a 224px dropdown longer than it is useful.
 */
export function useAccountMenuItems(): NavItem[] {
  const userMenuItems = useUserMenuItems();
  return useMemo(
    () => userMenuItems.filter((i) => i.to.startsWith('#') || i.trailing),
    [userMenuItems],
  );
}

/**
 * Built-in account destinations that always anchor the Account takeover's
 * nav, independent of which plugins contribute user-menu items. Plugin items
 * are appended (see {@link useAccountNavItems}) so the takeover carries the
 * full account option set — the dropdown deliberately carries less
 * ({@link useAccountMenuItems}).
 */
const BUILTIN_ACCOUNT_ITEMS: NavItem[] = [
  { to: ROUTES.profile, label: 'Profile', icon: User },
  { to: ROUTES.settings, label: 'Settings', icon: SlidersHorizontal },
];

/**
 * The full account option set, rendered as the Account takeover's nav:
 * built-in Profile/Settings plus the plugin-contributed user-menu items that
 * resolve to an in-app route. Hash commands (`#help-inspector`) and
 * `openInNewWindow` links are dropped — they are actions/off-site, not pages
 * the takeover would switch between.
 */
export function useAccountNavItems(): NavItem[] {
  const userMenuItems = useUserMenuItems();
  return useMemo(
    () => [
      ...BUILTIN_ACCOUNT_ITEMS,
      ...userMenuItems.filter((i) => !i.to.startsWith('#') && !i.openInNewWindow),
    ],
    [userMenuItems],
  );
}
